<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle pageTitle="Orders" subTitle="Transactions" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-0">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 p-4">
          <form class="position-relative table-src-form me-0" @submit.prevent>
            <input type="text" class="form-control" placeholder="Search orders..." v-model="searchQuery" />
            <i class="material-symbols-outlined position-absolute top-50 start-0 translate-middle-y">search</i>
          </form>
          <RouterLink to="/orders/create" class="btn btn-outline-primary py-1 px-2 px-sm-4 fs-14 fw-medium rounded-3 hover-bg">
            <span class="py-sm-1 d-block">
              <i class="ri-add-line d-none d-sm-inline-block me-1"></i>
              <span>Create New Order</span>
            </span>
          </RouterLink>
        </div>

        <div class="default-table-area style-two default-table-width">
          <div class="table-responsive">
            <table class="table align-middle">
              <thead>
                <tr>
                  <th scope="col">Order Code</th>
                  <th scope="col">Customer</th>
                  <th scope="col">Service Type</th>
                  <th scope="col">Total Price</th>
                  <th scope="col">Status</th>
                  <th scope="col">Processed By</th>
                  <th scope="col">Date</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="8" class="text-center py-4">Loading...</td>
                </tr>
                <tr v-else-if="filteredOrders.length === 0">
                  <td colspan="8" class="text-center py-4">No orders found.</td>
                </tr>
                <tr v-else v-for="order in filteredOrders" :key="order.order_id">
                  <td><RouterLink :to="`/orders/${order.order_id}`"><strong>{{ order.order_code || '#' + order.order_id }}</strong></RouterLink></td>
                  <td>
                    <div>{{ order.customer?.name || order.customer?.phone_number }}</div>
                    <small class="text-primary d-block fw-medium" v-if="order.customer?.nim" style="font-size: 11px;">
                      NIM: {{ order.customer.nim }}
                    </small>
                  </td>
                  <td>
                    <span class="badge bg-secondary">{{ order.service_type }}</span>
                    <span class="badge bg-primary-subtle text-primary d-block mt-1" v-if="order.service_classification" style="font-size: 10px;">
                      {{ order.service_classification }}
                    </span>
                  </td>
                  <td class="text-body">Rp {{ Number(order.total_price).toLocaleString('id-ID') }}</td>
                  <td>
                    <span class="badge bg-opacity-10 p-2 fs-12 fw-normal" :class="statusClass(order.order_status)">
                      {{ order.order_status }}
                    </span>
                  </td>
                  <td class="text-body">{{ order.processed_by?.name || '-' }}</td>
                  <td class="text-body">{{ new Date(order.order_date || order.created_at).toLocaleDateString() }}</td>
                  <td>
                    <div class="d-flex align-items-center gap-1">
                      <RouterLink :to="`/orders/${order.order_id}`" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-primary">visibility</i>
                      </RouterLink>
                      <button @click="deleteOrder(order.order_id)" v-if="isSuperAdmin" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-danger">delete</i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import PageTitle from '@/components/Common/PageTitle.vue';
import orderService from '@/services/orderService';

export default defineComponent({
  name: 'OrderListPage',
  components: {
    PageTitle
  },
  setup() {
    const orders = ref<any[]>([]);
    const loading = ref(true);
    const searchQuery = ref('');

    const isSuperAdmin = computed(() => {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      try {
        const user = JSON.parse(userStr);
        return user.role === 'super_admin';
      } catch (e) {
        return false;
      }
    });

    const fetchOrders = async () => {
      loading.value = true;
      try {
        const response = await orderService.getAll();
        orders.value = response.data;
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        loading.value = false;
      }
    };

    const deleteOrder = async (id: number) => {
      if (confirm('Are you sure you want to delete this order?')) {
        try {
          await orderService.delete(id);
          fetchOrders();
        } catch (error) {
          console.error('Error deleting order:', error);
        }
      }
    };

    const statusClass = (status: string) => {
      switch (status) {
        case 'Pending': return 'bg-warning text-warning';
        case 'Processing': return 'bg-primary text-primary';
        case 'Completed': return 'bg-success text-success';
        case 'Cancelled': return 'bg-danger text-danger';
        default: return 'bg-secondary text-secondary';
      }
    };

    const filteredOrders = computed(() => {
      let list = orders.value;
      if (searchQuery.value) {
        const q = searchQuery.value.toLowerCase();
        list = list.filter(o => 
          (o.order_code && o.order_code.toLowerCase().includes(q)) || 
          (o.customer?.name && o.customer.name.toLowerCase().includes(q)) ||
          (o.customer?.phone_number && o.customer.phone_number.toLowerCase().includes(q)) ||
          (o.customer?.nim && o.customer.nim.toLowerCase().includes(q)) ||
          o.service_type.toLowerCase().includes(q)
        );
      }
      return [...list].sort((a, b) => {
        const dateA = new Date(a.order_date || a.created_at).getTime() || 0;
        const dateB = new Date(b.order_date || b.created_at).getTime() || 0;
        if (dateA !== dateB) return dateB - dateA;
        return Number(b.order_id || 0) - Number(a.order_id || 0);
      });
    });

    onMounted(() => {
      fetchOrders();
    });

    return {
      orders,
      loading,
      searchQuery,
      filteredOrders,
      statusClass,
      deleteOrder,
      isSuperAdmin
    };
  }
});
</script>
